export const NO_DEPARTMENT_GROUP = '__none__';
export const MY_DEPARTMENT_GROUPS = '__mine__';

/**
 * Seeded, system-managed group (backend: Collections::AllDepartments) whose members receive the
 * cross-department batch summary email. It is synthetic — no real invoice is ever tagged with it —
 * so filtering by it matches nothing. Membership means "sees everything", which on these pages is
 * the existing 'all' option, so it is never offered as a department of its own.
 */
export const ALL_DEPARTMENTS_GROUP = 'ALL_DEPARTMENTS';

export interface DepartmentGroupRow {
  department_groups: string[];
  has_ungrouped_invoices: boolean;
}

/** Admin -> 'all'; exactly one group -> that group; 2+ -> "My groups"; no group -> 'all'. */
export const computeDefaultDepartmentGroup = (isCollectionsAdmin: boolean, myGroups: string[]): string => {
  if (isCollectionsAdmin || myGroups.length === 0) return 'all';
  if (myGroups.length === 1) return myGroups[0];
  return MY_DEPARTMENT_GROUPS;
};

/** "My groups" matches any row intersecting the user's groups; NO_DEPARTMENT_GROUP matches
 * rows with an ungrouped invoice; a specific group matches by membership; 'all' matches everything. */
export const matchesDepartmentGroup = (
  row: DepartmentGroupRow,
  departmentGroup: string,
  myGroups: string[],
): boolean => {
  if (departmentGroup === MY_DEPARTMENT_GROUPS) return row.department_groups.some((g) => myGroups.includes(g));
  if (departmentGroup === NO_DEPARTMENT_GROUP) return row.has_ungrouped_invoices;
  if (departmentGroup !== 'all') return row.department_groups.includes(departmentGroup);
  return true;
};

/** Dropdown options derived from loaded rows, always including the user's own groups so a
 * single-group default is selectable even when no loaded row falls into it. */
export const buildDepartmentGroupOptions = (
  rows: DepartmentGroupRow[],
  myGroups: string[],
): { sorted: string[]; hasUngrouped: boolean } => {
  const groups = new Set<string>();
  let hasUngrouped = false;
  rows.forEach((row) => {
    row.department_groups.forEach((g) => groups.add(g));
    if (row.has_ungrouped_invoices) hasUngrouped = true;
  });
  myGroups.forEach((g) => groups.add(g));
  return { sorted: Array.from(groups).sort((a, b) => a.localeCompare(b)), hasUngrouped };
};
